import React, { useState, useRef, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table, Modal, Spinner, Alert } from "react-bootstrap";
import { FaTrash, FaPlus } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./AddPurchase.css";

const EditDeliveryNote = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    customer_id: "",
    date: "",
    invoice_no: "",
    delivery_address: "",
    vehicle_no: "",
    driver_name: "",
    driver_phone: "",
    notes: "",
    status: "Pending",
    productSearch: "",
    selectedProduct: null,
    orderedQty: "",
    qty: ""
  });

  const [deliveryItems, setDeliveryItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedCustomerAddress, setSelectedCustomerAddress] = useState("");

  // Customer modal states
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    mobile: "",
    address: "",
    gst_no: "",
    state: "",
    city: "",
    pin_code: ""
  });
  const [customerErrors, setCustomerErrors] = useState({});
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [customerSuccess, setCustomerSuccess] = useState("");

  // Refs for input fields
  const customerRef = useRef(null);
  const dateRef = useRef(null);
  const invoiceNoRef = useRef(null);
  const deliveryAddressRef = useRef(null);
  const vehicleNoRef = useRef(null);
  const driverNameRef = useRef(null);
  const driverPhoneRef = useRef(null);
  const statusRef = useRef(null);
  const productSearchRef = useRef(null);
  const orderedQtyRef = useRef(null);
  const qtyRef = useRef(null);
  const notesRef = useRef(null);
  const submitBtnRef = useRef(null);

  const navigate = useNavigate();

  // Fetch data on component mount
  useEffect(() => {
    fetchAllData();
  }, [id]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch customers
      const customersResponse = await api.post("/customer_list.php", {});
      if (customersResponse.data.head.code === 200) {
        setCustomers(customersResponse.data.body);
      }

      // Fetch products
      const productsResponse = await api.post("/product_list.php", {});
      if (productsResponse.data.head.code === 200) {
        setProducts(productsResponse.data.body);
      }

      // Fetch invoices for dropdown
      const invoicesResponse = await api.post("/invoice_list.php", {});
      if (invoicesResponse.data.head.code === 200) {
        setInvoices(invoicesResponse.data.body);
      }

      // Fetch delivery note details
      const detailResponse = await api.post("/delivery_note_detail.php", { id });
      
      if (detailResponse.data.head.code === 200) {
        const noteData = detailResponse.data.body;
        setFormData({
          customer_id: noteData.customer_id || "",
          date: noteData.date || "",
          invoice_no: noteData.invoice_no || "",
          delivery_address: noteData.delivery_address || "",
          vehicle_no: noteData.vehicle_no || "",
          driver_name: noteData.driver_name || "",
          driver_phone: noteData.driver_phone || "",
          notes: noteData.notes || "",
          status: noteData.status || "Pending",
          productSearch: "",
          selectedProduct: null,
          orderedQty: "",
          qty: ""
        });

        // Set customer address
        if (noteData.customer_id) {
          const selectedCustomer = customers.find(c => c.id == noteData.customer_id);
          if (selectedCustomer) {
            const addressParts = [];
            if (selectedCustomer.address) addressParts.push(selectedCustomer.address);
            if (selectedCustomer.city) addressParts.push(selectedCustomer.city);
            if (selectedCustomer.state) addressParts.push(selectedCustomer.state);
            if (selectedCustomer.pin_code) addressParts.push(selectedCustomer.pin_code);
            setSelectedCustomerAddress(addressParts.join(", "));
          }
        }
        
        // Transform and set items
        const items = (noteData.items || []).map(item => ({
          product_code: item.product_id || item.product_code || "",
          order_qty: parseInt(item.order_qty) || 0,
          delivery_qty: parseInt(item.delivery_qty) || 0,
          pending_qty: parseInt(item.pending_qty) || 0,
          display_code: item.product_id || item.product_code || "",
          display_name: item.product_name || item.item_name || ""
        }));
        
        setDeliveryItems(items);
      } else {
        setSubmitError("Failed to load delivery note details");
      }

    } catch (err) {
      console.error("Error fetching data:", err);
      setSubmitError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredProducts = () => {
    if (!formData.productSearch) {
      return products.slice(0, 10);
    }
    
    const searchTerm = formData.productSearch.toLowerCase();
    const filtered = products.filter(product => {
      const productName = product.name || product.product_name || "";
      const productId = product.id ? product.id.toString() : "";
      return (
        productName.toLowerCase().includes(searchTerm) ||
        productId.toLowerCase().includes(searchTerm)
      );
    });
    return filtered.slice(0, 10);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'customer_id') {
      // Find selected customer to get their address
      const selectedCustomer = customers.find(c => c.id == value);
      if (selectedCustomer) {
        const addressParts = [];
        if (selectedCustomer.address) addressParts.push(selectedCustomer.address);
        if (selectedCustomer.city) addressParts.push(selectedCustomer.city);
        if (selectedCustomer.state) addressParts.push(selectedCustomer.state);
        if (selectedCustomer.pin_code) addressParts.push(selectedCustomer.pin_code);
        setSelectedCustomerAddress(addressParts.join(", "));
      } else {
        setSelectedCustomerAddress("");
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'productSearch') {
      setShowProductDropdown(true);
    }
  };

  const handleProductSelect = (product) => {
    const productId = product.id;
    const productName = product.name || product.product_name || "";
    const stock = product.stock || 0;
    
    setFormData(prev => ({
      ...prev,
      productSearch: `${productId} - ${productName}`,
      selectedProduct: {
        id: productId,
        product_code: productId, // Using ID as product code
        product_name: productName,
        stock: stock
      }
    }));
    setShowProductDropdown(false);
    orderedQtyRef.current.focus();
  };

  const handleAddItem = () => {
    if (!formData.selectedProduct || !formData.orderedQty || !formData.qty) {
      alert("Please fill all product fields before adding");
      return;
    }

    if (parseInt(formData.qty) > parseInt(formData.orderedQty)) {
      alert(`Delivery quantity cannot exceed ordered quantity! Ordered: ${formData.orderedQty}`);
      return;
    }

    const orderedQty = parseInt(formData.orderedQty);
    const deliveryQty = parseInt(formData.qty);
    const pendingQty = orderedQty - deliveryQty;

    const newItem = {
      product_code: formData.selectedProduct.id.toString(),
      order_qty: orderedQty,
      delivery_qty: deliveryQty,
      pending_qty: pendingQty,
      display_code: formData.selectedProduct.id.toString(), // Using ID as display code
      display_name: formData.selectedProduct.product_name
    };

    setDeliveryItems([...deliveryItems, newItem]);
    
    setFormData(prev => ({ 
      ...prev, 
      productSearch: "",
      selectedProduct: null,
      orderedQty: "",
      qty: ""
    }));
    
    productSearchRef.current.focus();
    setShowProductDropdown(false);
  };

  // Enter key navigation handler
  const handleKeyPress = (e, nextFieldRef, action = null) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (action) {
        action();
      } else if (nextFieldRef && nextFieldRef.current) {
        nextFieldRef.current.focus();
      }
    }
  };

  // Special handler for quantity field
  const handleQtyKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  // Special handler for notes field
  const handleNotesKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (deliveryItems.length > 0) {
        submitBtnRef.current.click();
      } else {
        alert("Please add at least one item before submitting");
      }
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...deliveryItems];
    
    if (field === 'order_qty' || field === 'delivery_qty') {
      const numValue = parseInt(value) || 0;
      
      if (field === 'delivery_qty') {
        const orderedQty = updatedItems[index].order_qty;
        if (numValue > orderedQty) {
          alert(`Delivery quantity cannot exceed ordered quantity! Ordered: ${orderedQty}`);
          return;
        }
      }
      
      if (field === 'order_qty') {
        const deliveredQty = updatedItems[index].delivery_qty;
        if (numValue < deliveredQty) {
          alert(`Ordered quantity cannot be less than already delivered quantity! Delivered: ${deliveredQty}`);
          return;
        }
      }
      
      updatedItems[index][field] = numValue;
      updatedItems[index].pending_qty = updatedItems[index].order_qty - updatedItems[index].delivery_qty;
    }
    
    setDeliveryItems(updatedItems);
  };

  const handleDeleteItem = (index) => {
    const updatedItems = deliveryItems.filter((_, i) => i !== index);
    setDeliveryItems(updatedItems);
  };

  // Customer Modal Functions
  const handleShowCustomerModal = () => {
    setShowCustomerModal(true);
    setCustomerErrors({});
    setCustomerSuccess("");
  };

  const handleCloseCustomerModal = () => {
    setShowCustomerModal(false);
    setNewCustomer({
      name: "",
      mobile: "",
      address: "",
      gst_no: "",
      state: "",
      city: "",
      pin_code: ""
    });
    setCustomerErrors({});
    setCustomerSuccess("");
    setIsAddingCustomer(false);
  };

  // Handle new customer form change
  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({ ...prev, [name]: value }));
    if (customerErrors[name]) {
      setCustomerErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Validate customer form
  const validateCustomer = () => {
    const errors = {};
    
    if (!newCustomer.name.trim()) {
      errors.name = "Customer name is required";
    }
    
    if (!newCustomer.mobile.trim()) {
      errors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(newCustomer.mobile)) {
      errors.mobile = "Enter a valid 10-digit mobile number";
    }
    
    return errors;
  };

  // Handle adding customer via API
  const handleAddCustomer = async () => {
    const validationErrors = validateCustomer();
    
    if (Object.keys(validationErrors).length > 0) {
      setCustomerErrors(validationErrors);
      return;
    }

    try {
      setIsAddingCustomer(true);
      setCustomerSuccess("");
      setCustomerErrors({});

      const payload = {
        name: newCustomer.name,
        mobile: newCustomer.mobile,
        gst_no: newCustomer.gst_no || "",
        state: newCustomer.state || "",
        city: newCustomer.city || "",
        address: newCustomer.address || "",
        pin_code: newCustomer.pin_code || ""
      };

      const response = await api.post("/customer_add.php", payload);

      if (response.data.head.code === 200) {
        const newCustomerObj = {
          id: response.data.body.customer_id,
          name: newCustomer.name,
          mobile: newCustomer.mobile,
          gst_no: newCustomer.gst_no,
          state: newCustomer.state,
          city: newCustomer.city,
          address: newCustomer.address,
          pin_code: newCustomer.pin_code
        };

        setCustomers(prev => [...prev, newCustomerObj]);
        
        setFormData(prev => ({ 
          ...prev, 
          customer_id: response.data.body.customer_id 
        }));

        const addressParts = [];
        if (newCustomer.address) addressParts.push(newCustomer.address);
        if (newCustomer.city) addressParts.push(newCustomer.city);
        if (newCustomer.state) addressParts.push(newCustomer.state);
        if (newCustomer.pin_code) addressParts.push(newCustomer.pin_code);
        setSelectedCustomerAddress(addressParts.join(", "));

        setCustomerSuccess(`Customer "${newCustomer.name}" added successfully!`);
        
        setTimeout(() => {
          handleCloseCustomerModal();
          if (customerRef.current) {
            customerRef.current.focus();
          }
        }, 2000);

      } else {
        const errorMsg = response.data.head.msg || "Failed to add customer";
        setCustomerErrors({ api: errorMsg });
      }
    } catch (err) {
      console.error("Error adding customer:", err);
      
      let errorMessage = "Failed to add customer. Please try again.";
      
      if (err.response) {
        errorMessage = err.response.data?.head?.msg || errorMessage;
      } else if (err.request) {
        errorMessage = "No response from server. Please check your connection.";
      }
      
      setCustomerErrors({ api: errorMessage });
    } finally {
      setIsAddingCustomer(false);
    }
  };

  // Handle keydown in customer modal
  const handleCustomerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddCustomer();
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.customer_id) newErrors.customer_id = "Customer is required";
    if (!formData.date) newErrors.date = "Date is required";
    if (deliveryItems.length === 0) newErrors.items = "At least one item is required";
    return newErrors;
  };

  const generateDeliveryNotePayload = () => {
    return {
      id: parseInt(id),
      customer_id: parseInt(formData.customer_id) || 0,
      date: formData.date,
      invoice_no: formData.invoice_no || "",
      delivery_address: formData.delivery_address || "",
      vehicle_no: formData.vehicle_no || "",
      driver_name: formData.driver_name || "",
      driver_phone: formData.driver_phone || "",
      notes: formData.notes || "",
      status: formData.status || "Pending",
      items: deliveryItems.map(item => ({
        product_id: item.product_code, // Changed from product_code to product_id
        order_qty: parseInt(item.order_qty),
        delivery_qty: parseInt(item.delivery_qty),
        pending_qty: parseInt(item.pending_qty)
      }))
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      setSubmitLoading(true);
      setSubmitError("");

      const payload = generateDeliveryNotePayload();
      
      const response = await api.post("/delivery_note_edit.php", payload);
      
      if (response.data.head.code === 200) {
        alert(`Delivery Note updated successfully!`);
        navigate("/delivery-note-list");
      } else {
        const errorMsg = response.data.head.msg || "Failed to update delivery note";
        setSubmitError(errorMsg);
      }
    } catch (err) {
      console.error("Error updating delivery note:", err);
      setSubmitError("Failed to update delivery note. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderError = (field) =>
    errors[field] ? <div className="text-danger small mt-1">{errors[field]}</div> : null;

  const renderCustomerError = (field) =>
    customerErrors[field] ? (
      <div className="text-danger small mt-1">{customerErrors[field]}</div>
    ) : null;

  if (loading) {
    return (
      <Container className="mt-4 text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading delivery note details...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Edit Delivery Note #{id}</h4>
        <Button className="btn-top" onClick={() => navigate("/delivery-note-list")} disabled={submitLoading}>
          Back
        </Button>
      </div>

      {submitError && (
        <Alert variant="danger" className="mb-3">
          {submitError}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} noValidate>
        <Row className="g-3">
          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Select Customer</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select
                  ref={customerRef}
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  onKeyPress={(e) => handleKeyPress(e, dateRef)}
                  disabled={submitLoading}
                  isInvalid={!!errors.customer_id}
                >
                  <option value="">Select Customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </Form.Select>
                <Button 
                  variant="outline-primary" 
                  onClick={handleShowCustomerModal}
                  className="d-flex align-items-center"
                  title="Add New Customer"
                  disabled={submitLoading}
                  type="button"
                >
                  <FaPlus />
                </Button>
              </div>
              {renderError("customer_id")}
            </Form.Group>
          </Col>

          <Col lg={5} md={5} sm={12}>
            <Form.Group>
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                value={selectedCustomerAddress}
                readOnly
                placeholder="Address will appear after selecting customer"
                className="address-field"
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={3} sm={12}>
            <Form.Group>
              <Form.Label>Date</Form.Label>
              <Form.Control
                ref={dateRef}
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                onKeyPress={(e) => handleKeyPress(e, invoiceNoRef)}
                disabled={submitLoading}
                isInvalid={!!errors.date}
              />
              {renderError("date")}
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Invoice No (Optional)</Form.Label>
              <Form.Select
                ref={invoiceNoRef}
                name="invoice_no"
                value={formData.invoice_no}
                onChange={handleChange}
                onKeyPress={(e) => handleKeyPress(e, deliveryAddressRef)}
                disabled={submitLoading}
              >
                <option value="">Select Invoice</option>
                {invoices.map((invoice) => (
                  <option key={invoice.id} value={invoice.id}>
                    INV-{invoice.id} - {invoice.customer_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Vehicle No</Form.Label>
              <Form.Control
                ref={vehicleNoRef}
                name="vehicle_no"
                type="text"
                value={formData.vehicle_no}
                onChange={handleChange}
                placeholder="Enter vehicle number"
                onKeyPress={(e) => handleKeyPress(e, driverNameRef)}
                disabled={submitLoading}
              />
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Driver Name</Form.Label>
              <Form.Control
                ref={driverNameRef}
                name="driver_name"
                type="text"
                value={formData.driver_name}
                onChange={handleChange}
                placeholder="Enter driver name"
                onKeyPress={(e) => handleKeyPress(e, driverPhoneRef)}
                disabled={submitLoading}
              />
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Driver Phone</Form.Label>
              <Form.Control
                ref={driverPhoneRef}
                name="driver_phone"
                type="text"
                value={formData.driver_phone}
                onChange={handleChange}
                placeholder="Enter driver phone"
                onKeyPress={(e) => handleKeyPress(e, statusRef)}
                disabled={submitLoading}
              />
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                ref={statusRef}
                name="status"
                value={formData.status}
                onChange={handleChange}
                onKeyPress={(e) => handleKeyPress(e, productSearchRef)}
                disabled={submitLoading}
              >
                <option value="Pending">Pending</option>
                <option value="In Transit">In Transit</option>
                <option value="Delivered">Delivered</option>
                <option value="Cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col lg={12} md={12} sm={12}>
            <Form.Group>
              <Form.Label>Delivery Address</Form.Label>
              <Form.Control
                ref={deliveryAddressRef}
                as="textarea"
                rows={2}
                name="delivery_address"
                value={formData.delivery_address}
                onChange={handleChange}
                placeholder="Enter delivery address"
                disabled={submitLoading}
              />
            </Form.Group>
          </Col>

          {/* Add Product Section */}
          <Col lg={12}>
            <hr />
            <h5>Add New Product</h5>
          </Col>

          <Col lg={4} md={4} sm={12}>
            <Form.Group className="position-relative">
              <Form.Label>Search Product</Form.Label>
              <Form.Control
                ref={productSearchRef}
                name="productSearch"
                type="text"
                value={formData.productSearch}
                onChange={handleChange}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                onKeyPress={(e) => handleKeyPress(e, orderedQtyRef)}
                placeholder="Type Product ID or Name..."
                autoComplete="off"
                disabled={submitLoading}
              />
              {showProductDropdown && (
                <div className="custom-dropdown">
                  {getFilteredProducts().map((product) => {
                    const productName = product.name || product.product_name || "";
                    const productId = product.id ? product.id.toString() : "";
                    const stock = product.stock || 0;
                    return (
                      <div
                        key={product.id}
                        className="dropdown-item"
                        onClick={() => handleProductSelect(product)}
                      >
                        <div className="fw-bold">{productId} - {productName}</div>
                        <div className="small text-muted">Stock: {stock}</div>
                      </div>
                    );
                  })}
                  {getFilteredProducts().length === 0 && (
                    <div className="dropdown-item text-muted">
                      No products found
                    </div>
                  )}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={6}>
            <Form.Group>
              <Form.Label>Ordered Qty</Form.Label>
              <Form.Control
                ref={orderedQtyRef}
                name="orderedQty"
                type="number"
                value={formData.orderedQty}
                onChange={handleChange}
                placeholder="0"
                min="0"
                onKeyPress={(e) => handleKeyPress(e, qtyRef)}
                disabled={submitLoading}
              />
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={6}>
            <Form.Group>
              <Form.Label>Delivery Qty</Form.Label>
              <Form.Control
                ref={qtyRef}
                name="qty"
                type="number"
                value={formData.qty}
                onChange={handleChange}
                placeholder="0"
                min="0"
                onKeyPress={handleQtyKeyPress}
                disabled={submitLoading}
              />
              <small className="text-muted">Press Enter to add item</small>
            </Form.Group>
          </Col>

          <Col lg={12} md={12} sm={12}>
            <Form.Group>
              <Form.Label>Notes (Optional)</Form.Label>
              <Form.Control
                ref={notesRef}
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Enter any notes..."
                onKeyPress={handleNotesKeyPress}
                disabled={submitLoading}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Delivery Items Table */}
        <Row className="mt-4">
          <Col xs={12}>
            <div className="table-responsive">
              <Table bordered className="purchase-table">
                <thead className="table-dark">
                  <tr>
                    <th width="5%">Sno</th>
                    <th width="25%">Product Name</th>
                    <th width="15%">Product ID</th>
                    <th width="15%">Ordered Qty</th>
                    <th width="15%">Delivery Qty</th>
                    <th width="15%">Pending Qty</th>
                    <th width="10%">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryItems.map((item, index) => (
                    <tr key={index}>
                      <td className="text-center">{index + 1}</td>
                      <td>{item.display_name}</td>
                      <td className="text-center">{item.display_code}</td>
                      <td>
                        <Form.Control
                          type="number"
                          className="right-align"
                          value={item.order_qty}
                          onChange={(e) => handleItemChange(index, 'order_qty', e.target.value)}
                          placeholder="0"
                          min="0"
                          disabled={submitLoading}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={item.delivery_qty}
                          className="right-align"
                          onChange={(e) => handleItemChange(index, 'delivery_qty', e.target.value)}
                          placeholder="0"
                          min="0"
                          disabled={submitLoading}
                        />
                      </td>
                      <td className="text-center">
                        <span className={item.pending_qty > 0 ? "text-warning fw-bold" : "text-success"}>
                          {item.pending_qty}
                        </span>
                      </td>
                      <td className="text-center">
                        <FaTrash
                          className="delete-icon"
                          style={{ cursor: submitLoading ? 'not-allowed' : 'pointer', color: '#dc3545', opacity: submitLoading ? 0.5 : 1 }}
                          onClick={() => !submitLoading && handleDeleteItem(index)}
                          title="Delete Item"
                        />
                      </td>
                    </tr>
                  ))}
                  {deliveryItems.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-3">
                        No items added. Add items using the form above.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>

        {renderError("items")}

        {/* Totals Summary */}
        {deliveryItems.length > 0 && (
          <Row className="mt-4">
            <Col xs={12} className="d-flex justify-content-end">
              <div className="totals-section">
                <div className="total-row">
                  <span className="total-label">Total Ordered Qty:</span>
                  <span className="total-value">
                    {deliveryItems.reduce((total, item) => total + (item.order_qty || 0), 0)}
                  </span>
                </div>
                <div className="total-row">
                  <span className="total-label">Total Delivery Qty:</span>
                  <span className="total-value">
                    {deliveryItems.reduce((total, item) => total + (item.delivery_qty || 0), 0)}
                  </span>
                </div>
                <div className="total-row grand-total">
                  <span className="total-label">Total Pending Qty:</span>
                  <span className="total-value">
                    {deliveryItems.reduce((total, item) => total + (item.pending_qty || 0), 0)}
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        )}

        <div className="d-flex justify-content-end mt-4 gap-2">
          <Button 
            ref={submitBtnRef}
            type="submit" 
            className="btn-submit"
            disabled={submitLoading || deliveryItems.length === 0}
          >
            {submitLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Update Delivery Note"
            )}
          </Button>
          <Button 
            type="button" 
            className="btn-top" 
            onClick={() => navigate("/delivery-note-list")}
            disabled={submitLoading}
          >
            Cancel
          </Button>
        </div>
      </Form>

      {/* Add Customer Modal (same as before) */}
      <Modal show={showCustomerModal} onHide={handleCloseCustomerModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customerSuccess && (
            <Alert variant="success" className="mb-3">
              {customerSuccess}
            </Alert>
          )}

          {customerErrors.api && (
            <Alert variant="danger" className="mb-3">
              {customerErrors.api}
            </Alert>
          )}

          <Form onKeyDown={handleCustomerKeyDown}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={newCustomer.name}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter customer name"
                    required
                    isInvalid={!!customerErrors.name}
                    autoFocus
                  />
                  {renderCustomerError("name")}
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile *</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={newCustomer.mobile}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter 10-digit mobile number"
                    maxLength="10"
                    required
                    isInvalid={!!customerErrors.mobile}
                  />
                  {renderCustomerError("mobile")}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="address"
                value={newCustomer.address}
                onChange={handleNewCustomerChange}
                placeholder="Enter address"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={newCustomer.city}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter city"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={newCustomer.state}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter state"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Pin Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="pin_code"
                    value={newCustomer.pin_code}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter pin code"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Number (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="gst_no"
                    value={newCustomer.gst_no}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter GST number"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleCloseCustomerModal}
            disabled={isAddingCustomer}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddCustomer} 
            disabled={!newCustomer.name.trim() || !newCustomer.mobile.trim() || isAddingCustomer}
          >
            {isAddingCustomer ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              "Add Customer"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EditDeliveryNote;